import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8–19
const QUARTERS = ['00', '15', '30', '45'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

export default function HeatmapPage() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resLoading, setResLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/resources`, { withCredentials: true })
      .then(res => {
        setResources(res.data);
        if (res.data.length > 0 && !selectedResource) {
          setSelectedResource(res.data[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setResLoading(false));
  }, []);

  const fetchHeatmap = useCallback(async () => {
    if (!selectedResource || !weekStart) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/bookings/heatmap`, {
        params: {
          resource_id: selectedResource,
          week_start: format(weekStart, 'yyyy-MM-dd'),
        },
        withCredentials: true,
      });
      setHeatmap(data.heatmap);
    } catch {
      setHeatmap(null);
    } finally {
      setLoading(false);
    }
  }, [selectedResource, weekStart]);

  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);

  const prevWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    setWeekStart(prev);
  };

  const nextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    setWeekStart(next);
  };

  const thisWeek = () => setWeekStart(getMonday(new Date()));

  const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const now = useMemo(() => new Date(), []);
  const nowTotalMin = useMemo(() => now.getHours() * 60 + now.getMinutes(), [now]);
  const currentRowIndex = useMemo(() => {
    const hour = now.getHours();
    const quarterIndex = Math.floor(now.getMinutes() / 15);
    if (hour < 8 || hour > 19) return -1;
    return (hour - 8) * 4 + quarterIndex;
  }, [now]);

  // Build lookup: `${date}_${hour}_${quarter}` → { booked, booked_by }
  const cellMap = useMemo(() => {
    const map = {};
    if (!heatmap) return map;
    heatmap.forEach(day => {
      day.slots.forEach(slot => {
        const [h, m] = slot.time.split(':').map(Number);
        const quarter = m.toString().padStart(2, '0');
        const key = `${day.date}_${h}_${quarter}`;
        if (!map[key]) map[key] = { booked: false, booked_by: null };
        if (slot.booked) {
          map[key].booked = true;
          map[key].booked_by = slot.booked_by;
        }
      });
    });
    return map;
  }, [heatmap]);

  const getCellInfo = useCallback((date, hour, quarter) => {
    const key = `${date}_${hour}_${quarter}`;
    const cell = cellMap[key];

    // Date string comparison — avoid UTC midnight issues
    const cellDateStr = format(new Date(`${date}T00:00:00`), 'yyyy-MM-dd');

    if (cellDateStr < todayStr) {
      return { status: 'past' };
    }

    if (cellDateStr === todayStr) {
      const cellMin = hour * 60 + parseInt(quarter, 10);
      if (cellMin < nowTotalMin) {
        return { status: 'past' };
      }
      if (cellMin === nowTotalMin) {
        return { status: 'now' };
      }
    }

    if (!cell) return { status: 'no-data' };
    if (cell.booked) return { status: 'booked', bookedBy: cell.booked_by };
    return { status: 'available' };
  }, [cellMap, todayStr, nowTotalMin]);

  const handleCellClick = (date, hour, quarter) => {
    const info = getCellInfo(date, hour, quarter);
    if (info.status !== 'available') return;
    const timeStr = `${hour.toString().padStart(2, '0')}:${quarter}`;
    navigate(`/book/${selectedResource}?date=${date}&time=${timeStr}`);
  };

  const weekDates = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd')),
    [weekStart]
  );

  if (resLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={24} className="animate-spin text-text-secondary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="heatmap-page">

      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tighter text-white">
            Availability
          </h1>
          <p className="text-text-secondary text-sm mt-2 font-body">
            Click a slot to book — 15 minute intervals.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {resources.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedResource(r.id)}
              className={`px-3 py-1.5 text-xs font-mono border transition-all duration-200 ${
                selectedResource === r.id
                  ? 'bg-white text-black border-white'
                  : 'bg-surface border-border text-text-secondary hover:text-white hover:border-white/30'
              }`}
              data-testid={`resource-btn-${r.id}`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={prevWeek}
          className="p-2 border border-border text-text-secondary hover:text-white hover:border-white/30 transition-all"
          data-testid="prev-week-btn"
          aria-label="Previous week"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-white">
            {format(weekStart, 'MMM d')} — {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </span>
          <button
            onClick={thisWeek}
            className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest border border-border text-text-secondary hover:text-white hover:border-white/30 transition-all"
            data-testid="this-week-btn"
          >
            This week
          </button>
        </div>
        <button
          onClick={nextWeek}
          className="p-2 border border-border text-text-secondary hover:text-white hover:border-white/30 transition-all"
          data-testid="next-week-btn"
          aria-label="Next week"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-6 text-xs font-mono flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 rounded-sm bg-available" />
          <span className="text-text-secondary">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 rounded-sm bg-booked" />
          <span className="text-text-secondary">Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 rounded-sm bg-surface-hover border border-border" />
          <span className="text-text-secondary">Past</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 rounded-sm bg-now" />
          <span className="text-text-secondary">Now</span>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={20} className="animate-spin text-text-secondary" />
        </div>
      ) : (
        <div className="border border-border overflow-hidden rounded-lg" data-testid="heatmap-grid">

          {/* Day header row */}
          <div
            className="grid bg-surface border-b border-border"
            style={{ gridTemplateColumns: '64px repeat(7, 1fr)' }}
          >
            <div className="p-3 border-r border-border" />
            {weekDates.map((date, i) => {
              const isToday = date === todayStr;
              const dayNum = new Date(date + 'T00:00:00').getDate();
              return (
                <div
                  key={date}
                  className={`p-3 text-center border-r border-border last:border-r-0 ${isToday ? 'bg-accent/10' : ''}`}
                >
                  <div className={`text-[10px] font-mono uppercase tracking-widest ${isToday ? 'text-accent' : 'text-text-secondary'}`}>
                    {DAYS[i]}
                  </div>
                  <div className={`text-2xl font-heading font-medium mt-0.5 ${isToday ? 'text-white' : 'text-text-secondary'}`}>
                    {dayNum}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time grid — each row is one quarter slot */}
          {HOURS.map((hour, hi) => {
            return QUARTERS.map((quarter, qi) => {
              const rowIndex = hi * 4 + qi;
              const isNowRow = rowIndex === currentRowIndex;
              const timeLabel = `${hour.toString().padStart(2, '0')}:${quarter}`;

              return (
                <div
                  key={`${hour}-${quarter}`}
                  className={`grid border-b border-border/60 ${isNowRow ? 'bg-now/10' : ''}`}
                  style={{ gridTemplateColumns: '64px repeat(7, 1fr)', height: '28px' }}
                >
                  {/* Time label */}
                  <div
                    className={`flex items-center px-3 border-r border-border ${isNowRow ? 'text-white' : 'text-text-secondary'}`}
                  >
                    {qi === 0 && (
                      <span className="text-[11px] font-mono">{timeLabel}</span>
                    )}
                  </div>

                  {/* Day cells */}
                  {weekDates.map((date, di) => {
                    const info = getCellInfo(date, hour, quarter);
                    const isToday = date === todayStr;
                    const isLastCol = di === 6;

                    let inner = null;
                    let cellClass = 'relative flex items-center justify-center border-r border-border/60 last:border-r-0';

                    if (isLastCol) {
                      cellClass = cellClass.replace('border-r-0', '').trim() + ' border-r-0';
                    }

                    if (info.status === 'past') {
                      inner = <div className="w-full h-full bg-surface-hover" />;
                    } else if (info.status === 'now') {
                      inner = (
                        <div
                          className="absolute inset-0 bg-now/40 border-2 border-now"
                          title="Current time"
                        />
                      );
                    } else if (info.status === 'booked') {
                      inner = (
                        <div
                          className="w-full h-full bg-booked/80 flex items-center justify-center"
                          title={`Booked by ${info.bookedBy || 'unknown'}`}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-booked" />
                        </div>
                      );
                    } else if (info.status === 'available') {
                      inner = (
                        <button
                          onClick={() => handleCellClick(date, hour, quarter)}
                          className="w-full h-full bg-available hover:bg-available/70 flex items-center justify-center transition-colors cursor-pointer"
                          title={`Book ${date} at ${timeLabel}`}
                          data-testid={`slot-${date}-${hour}-${quarter}`}
                        />
                      );
                    } else {
                      // no-data — visible placeholder so column is perceivable
                      inner = <div className="w-full h-full bg-surface" />;
                    }

                    return (
                      <div key={date} className={cellClass}>
                        {inner}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })}
        </div>
      )}

      {/* Book CTA */}
      {selectedResource && (
        <div className="mt-8 text-center">
          <Link
            to={`/book/${selectedResource}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-body font-medium text-sm hover:opacity-90 transition-all"
            data-testid="book-resource-btn"
          >
            <Calendar size={16} />
            Book {resources.find(r => r.id === selectedResource)?.name}
          </Link>
        </div>
      )}
    </div>
  );
}
